import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon, 
  ClockIcon
} from '@heroicons/react/24/outline';
import { contactService, ContactInfo } from '../services/contactService';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const Contact = () => {
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        setIsLoading(true);
        const data = await contactService.getContactInfo();
        setContactInfo(data);
      } catch (err) {
        console.error('Error loading contact information:', err);
        setError('Failed to load contact information.');
      } finally {
        setIsLoading(false);
      }
    };

    loadContactInfo();
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Display loading spinner while fetching data
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  // Display error message if there's an error
  if (error || !contactInfo) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="bg-red-50 p-4 rounded-md text-red-700 max-w-md mx-auto">
          <p>{error || 'Unable to load contact information.'}</p>
        </div>
      </div>
    );
  }

  // Format the full address
  const fullAddress = `${contactInfo.address.street}, ${contactInfo.address.city}, ${contactInfo.address.state} ${contactInfo.address.zipCode}`;

  return (
    <div className="bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <div className="relative bg-primary-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src="/images/hero/hero-1.jpg" 
            alt="Contact us background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-primary-900 opacity-85"></div>
        </div>
        
        <div className="container mx-auto px-4 py-24 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="max-w-2xl mx-auto text-center"
          >
            <h1 className="text-5xl font-display font-bold mb-6">Get In Touch</h1>
            <p className="text-xl text-primary-100 mb-8">
              We'd love to hear from you. Contact us using the information below and 
              we'll get back to you as soon as possible.
            </p>
            <a 
              href={`tel:${contactInfo.phone.replace(/\D/g, '')}`}
              className="inline-flex items-center px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-medium rounded-md transition duration-300 transform hover:scale-105"
            >
              <PhoneIcon className="w-5 h-5 mr-2" />
              Call Us Today
            </a>
          </motion.div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="container mx-auto px-4 py-16">
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="max-w-3xl mx-auto"
        >
          <motion.div 
            variants={fadeIn}
            className="mb-12"
          >
            <h2 className="text-3xl font-semibold text-primary-900 mb-6 text-center">Contact Information</h2>
            <p className="text-primary-600 mb-8 text-center">
              Ready to start your dream home project? Our team of experts is ready to help
              you bring your vision to life. Reach out to us today!
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div 
                variants={fadeIn}
                className="flex items-start"
              >
                <div className="flex-shrink-0 bg-accent-100 rounded-full p-3 mr-4">
                  <PhoneIcon className="h-6 w-6 text-accent-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-primary-900">Phone</h3>
                  <p className="text-primary-600 mt-1">
                    <a href={`tel:${contactInfo.phone.replace(/\D/g, '')}`} className="hover:text-accent-600 transition-colors">
                      {contactInfo.phone}
                    </a>
                  </p>
                </div>
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                className="flex items-start"
              >
                <div className="flex-shrink-0 bg-accent-100 rounded-full p-3 mr-4">
                  <EnvelopeIcon className="h-6 w-6 text-accent-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-primary-900">Email</h3>
                  <p className="text-primary-600 mt-1">
                    <a href={`mailto:${contactInfo.email}`} className="hover:text-accent-600 transition-colors">
                      {contactInfo.email}
                    </a>
                  </p>
                </div>
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                className="flex items-start"
              >
                <div className="flex-shrink-0 bg-accent-100 rounded-full p-3 mr-4">
                  <MapPinIcon className="h-6 w-6 text-accent-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-primary-900">Office Address</h3>
                  <p className="text-primary-600 mt-1">
                    {contactInfo.address.street}<br />
                    {contactInfo.address.city}, {contactInfo.address.state} {contactInfo.address.zipCode}
                  </p>
                </div>
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                className="flex items-start"
              >
                <div className="flex-shrink-0 bg-accent-100 rounded-full p-3 mr-4">
                  <ClockIcon className="h-6 w-6 text-accent-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-primary-900">Business Hours</h3>
                  <p className="text-primary-600 mt-1">
                    Monday: {contactInfo.businessHours.monday}<br />
                    Tuesday: {contactInfo.businessHours.tuesday}<br />
                    Wednesday: {contactInfo.businessHours.wednesday}<br />
                    Thursday: {contactInfo.businessHours.thursday}<br />
                    Friday: {contactInfo.businessHours.friday}<br />
                    Saturday: {contactInfo.businessHours.saturday}<br />
                    Sunday: {contactInfo.businessHours.sunday}
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-primary-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl font-semibold mb-6">Ready to Build Your Dream Home?</h2>
            <p className="text-primary-200 mb-8 text-lg">
              Our team of experts is ready to help you bring your vision to life.
              Contact us today to schedule a consultation.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
              <a
                href={`tel:${contactInfo.phone.replace(/\D/g, '')}`}
                className="inline-flex items-center justify-center px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-medium rounded-md transition duration-300"
              >
                <PhoneIcon className="w-5 h-5 mr-2" />
                {contactInfo.phone}
              </a>
              <a
                href={`mailto:${contactInfo.email}`}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-primary-800 hover:bg-gray-100 font-medium rounded-md transition duration-300"
              >
                <EnvelopeIcon className="w-5 h-5 mr-2" />
                Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact; 